import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  Grid,
  Spinner,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAuthStore } from '@/store/auth'
import { SearchableSelect } from '@/components/common/SearchableSelect'
import {
  createBookingSchema,
  updateBookingSchema,
  type CreateBookingFormData,
  type UpdateBookingFormData,
} from '@/lib/validations/auth'

interface DropdownData {
  id: number
  name: string
}

interface BookingFormProps {
  bookingId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type BookingFormValues = CreateBookingFormData | UpdateBookingFormData

export function BookingForm({ bookingId, onSuccess, onCancel }: BookingFormProps) {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()
  const currentUserId = useAuthStore((state) => state.user?.id)

  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = !!bookingId

  const [shippingLines, setShippingLines] = useState<DropdownData[]>([])
  const [cypa, setCypa] = useState<DropdownData[]>([])

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm<BookingFormValues>({
    resolver: zodResolver(isEditMode ? updateBookingSchema : createBookingSchema),
    defaultValues: {
      remarks: 'SHIP IN',
    },
  })

  const cypaIdFrom = watch('cypa_id_from')
  const cypaIdTo = watch('cypa_id_to')

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      try {
        let bookingData: Record<string, unknown> = {}
        if (isEditMode && bookingId) {
          const { data } = await apiClient.get(`/bookings/${bookingId}`)
          bookingData = (data?.data || data || {}) as Record<string, unknown>
        }

        const [shippingLineRes, cypaRes] = await Promise.all([
          apiClient.get('/shipping-lines'),
          apiClient.get('/container-yards/yard-list', { params: { is_active: 1 } }),
        ])

        let shippingLineData = (shippingLineRes.data?.data || shippingLineRes.data || []) as DropdownData[]
        let cypaData = (cypaRes.data?.data || cypaRes.data || []) as DropdownData[]

        if (isEditMode && bookingData) {
          const shippingLineId = bookingData.shipping_line_id as number | undefined
          const shippingLine = bookingData.shipping_line as { id?: number; name?: string } | undefined
          if (shippingLineId != null && !shippingLineData.some((s) => s.id === shippingLineId)) {
            shippingLineData = [
              { id: shippingLineId, name: shippingLine?.name ?? 'Unknown' },
              ...shippingLineData,
            ]
          }
          const cypaIdFrom = bookingData.cypa_id_from as number | undefined
          const cypaFrom = bookingData.cypa_from as { id?: number; name?: string } | undefined
          if (cypaIdFrom != null && !cypaData.some((c) => c.id === cypaIdFrom)) {
            cypaData = [{ id: cypaIdFrom, name: cypaFrom?.name ?? 'Unknown' }, ...cypaData]
          }
          const cypaIdTo = bookingData.cypa_id_to as number | undefined
          const cypaTo = bookingData.cypa_to as { id?: number; name?: string } | undefined
          if (cypaIdTo != null && !cypaData.some((c) => c.id === cypaIdTo)) {
            cypaData = [{ id: cypaIdTo, name: cypaTo?.name ?? 'Unknown' }, ...cypaData]
          }
        }

        setShippingLines(shippingLineData)
        setCypa(cypaData)

        if (isEditMode && bookingId && Object.keys(bookingData).length > 0) {
          const isShipIn = bookingData.is_ship_in !== false
          reset({
            reference_number: (bookingData.reference_number as string) || '',
            shipping_line_id: (bookingData.shipping_line_id as number) || undefined,
            cypa_id_from: (bookingData.cypa_id_from as number) || undefined,
            cypa_id_to: (bookingData.cypa_id_to as number) || undefined,
            expected_date: (bookingData.expected_date as string) || '',
            expected_container: (bookingData.expected_container as number) || undefined,
            vessel: (bookingData.vessel as string) || '',
            remarks: isShipIn ? 'SHIP IN' : 'SHIP OUT',
          })
        }
      } catch (error: any) {
        showError(error)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [isEditMode, bookingId, reset, showError])

  const onSubmit = async (data: BookingFormValues) => {
    setIsSubmitting(true)

    try {
      const payload = {
        reference_number: data.reference_number,
        shipping_line_id: data.shipping_line_id,
        cypa_id_from: data.cypa_id_from,
        cypa_id_to: data.cypa_id_to,
        expected_date: data.expected_date,
        expected_container: data.expected_container,
        vessel: data.vessel,
        is_ship_in: data.remarks === 'SHIP IN',
        prepared_by: currentUserId ?? undefined,
      }

      if (isEditMode) {
        await apiClient.put(`/bookings/${bookingId}`, payload)
        showSuccess('Success', 'Booking updated successfully')
      } else {
        await apiClient.post('/bookings', payload)
        showSuccess('Success', 'Booking created successfully')
      }

      if (onSuccess) {
        onSuccess()
      } else {
        navigate('/bookings')
      }
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/bookings')
    }
  }

  if (isLoading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text>Loading...</Text>
      </VStack>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={6} align="stretch">
        {/* Reference no */}
        <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
          <FormControl isInvalid={!!errors.reference_number}>
            <FormLabel fontWeight="medium">
              Reference Number
            </FormLabel>
            <Input
              {...register('reference_number')}
              placeholder="Reference no"
              _placeholder={{ fontSize: 'sm' }}
            />
            <FormErrorMessage>{errors.reference_number?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        {/* Client Information Section */}
        <Box>
          <Text fontSize="md" fontWeight="semibold" color="gray.500" mb={4}>
            Client Information
          </Text>
          <VStack spacing={4} align="stretch">
            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
              <FormControl isInvalid={!!errors.shipping_line_id}>
                <FormLabel fontWeight="medium">
                  Shipping Line <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <Controller
                  name="shipping_line_id"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      options={shippingLines}
                      optionsUrl="/shipping-lines"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      placeholder="Select Shipping Line"
                      isLoading={isLoading}
                      isInvalid={!!errors.shipping_line_id}
                    />
                  )}
                />
                <FormErrorMessage>{errors.shipping_line_id?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.vessel}>
                <FormLabel fontWeight="medium">Vessel</FormLabel>
                <Input
                  {...register('vessel')}
                  placeholder="Vessel"
                  _placeholder={{ fontSize: 'sm' }}
                />
                <FormErrorMessage>{errors.vessel?.message}</FormErrorMessage>
              </FormControl>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
              <FormControl isInvalid={!!errors.cypa_id_from}>
                <FormLabel fontWeight="medium">
                  Pickup <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <Controller
                  name="cypa_id_from"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      options={cypa.filter((item) => item.id !== cypaIdTo)}
                      optionsUrl="/container-yards/yard-list"
                      optionsParams={{ is_active: 1 }}
                      excludeOptionId={cypaIdTo}
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      placeholder="Select pickup location"
                      isLoading={isLoading}
                      isInvalid={!!errors.cypa_id_from}
                    />
                  )}
                />
                <FormErrorMessage>{errors.cypa_id_from?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.cypa_id_to}>
                <FormLabel fontWeight="medium">
                  Dropoff <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <Controller
                  name="cypa_id_to"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      options={cypa.filter((item) => item.id !== cypaIdFrom)}
                      optionsUrl="/container-yards/yard-list"
                      optionsParams={{ is_active: 1 }}
                      excludeOptionId={cypaIdFrom}
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      placeholder="Select dropoff location"
                      isLoading={isLoading}
                      isInvalid={!!errors.cypa_id_to}
                    />
                  )}
                />
                <FormErrorMessage>{errors.cypa_id_to?.message}</FormErrorMessage>
              </FormControl>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
              <FormControl isInvalid={!!errors.expected_date}>
                <FormLabel fontWeight="medium">
                  Expected Date <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <Input
                  type="date"
                  {...register('expected_date')}
                  placeholder="Select Expected Date"
                  _placeholder={{ fontSize: 'sm' }}
                />
                <FormErrorMessage>{errors.expected_date?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.expected_container}>
                <FormLabel fontWeight="medium">
                  Expected Container <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <Input
                  type="number"
                  {...register('expected_container', {
                    valueAsNumber: true,
                  })}
                  placeholder="Enter expected number of containers"
                  _placeholder={{ fontSize: 'sm' }}
                  min={1}
                />
                <FormErrorMessage>{errors.expected_container?.message}</FormErrorMessage>
              </FormControl>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
              <FormControl isInvalid={!!errors.remarks}>
                <FormLabel fontWeight="medium">Remarks</FormLabel>
                <Select
                  {...register('remarks')}
                  placeholder="Select remarks"
                >
                  <option value="SHIP IN">SHIP IN</option>
                  <option value="SHIP OUT">SHIP OUT</option>
                </Select>
                <FormErrorMessage>{errors.remarks?.message}</FormErrorMessage>
              </FormControl>
            </Grid>
          </VStack>
        </Box>

        <Box height={1} />

        {/* Submit Button */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={handleCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
          >
            {isEditMode ? 'Save' : 'Create'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
